import { useState, useEffect } from "react";
import { Search, UserPlus, Shield, Ban, Eye, MoreHorizontal, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/users?search=${search}&limit=50`, { withCredentials: true })
      .then(r => { setUsers(r.data.users || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [search]);

  const filtered = tab === "all" ? users : tab === "admins" ? users.filter(u => u.role === "admin") : users.filter(u => u.role === "user");

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Users ({total})</h1>
        <Button className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="add-admin-btn"><UserPlus className="w-4 h-4 mr-1" /> Add Admin</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-white rounded-xl border"><TabsTrigger value="all" className="rounded-xl text-xs">All Users ({users.length})</TabsTrigger><TabsTrigger value="users" className="rounded-xl text-xs">Customers ({users.filter(u => u.role === "user").length})</TabsTrigger><TabsTrigger value="admins" className="rounded-xl text-xs">Admins ({users.filter(u => u.role === "admin").length})</TabsTrigger></TabsList>
      </Tabs>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." className="pl-10 rounded-xl" data-testid="search-users" />
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50"><th className="p-4 font-medium">User</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Phone</th><th className="p-4 font-medium">Role</th><th className="p-4 font-medium">Wallet</th><th className="p-4 font-medium">Joined</th><th className="p-4 font-medium">Actions</th></tr></thead>
          <tbody>
            {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={7} className="p-4"><div className="h-10 skeleton-loading rounded" /></td></tr>) :
            filtered.map(u => (
              <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="p-4"><div className="flex items-center gap-3">{u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 text-xs font-bold">{u.name?.[0]}</div>}<span className="font-medium text-slate-800">{u.name}</span></div></td>
                <td className="p-4 text-slate-600">{u.email}</td>
                <td className="p-4 text-slate-600">{u.phone || "-"}</td>
                <td className="p-4"><Badge className={`text-xs ${u.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-700"}`}>{u.role}</Badge></td>
                <td className="p-4 font-medium">Rs.{u.wallet_balance || 0}</td>
                <td className="p-4 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                <td className="p-4"><div className="flex gap-1"><button onClick={() => setSelected(u)} className="p-1.5 hover:bg-cyan-50 rounded-lg text-slate-500 hover:text-cyan-600"><Eye className="w-4 h-4" /></button></div></td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No users found</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle>User Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {selected.picture ? <img src={selected.picture} alt="" className="w-16 h-16 rounded-full" /> : <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 text-2xl font-bold">{selected.name?.[0]}</div>}
                <div><p className="font-semibold text-lg">{selected.name}</p><p className="text-sm text-slate-500">{selected.email}</p><Badge className="mt-1">{selected.role}</Badge></div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-sky-50 rounded-xl p-3"><p className="text-slate-500 text-xs">Phone</p><p className="font-medium">{selected.phone || "Not set"}</p></div>
                <div className="bg-sky-50 rounded-xl p-3"><p className="text-slate-500 text-xs">Wallet</p><p className="font-medium">Rs.{selected.wallet_balance || 0}</p></div>
                <div className="bg-sky-50 rounded-xl p-3"><p className="text-slate-500 text-xs">Referral Code</p><p className="font-medium">{selected.referral_code || "-"}</p></div>
                <div className="bg-sky-50 rounded-xl p-3"><p className="text-slate-500 text-xs">Joined</p><p className="font-medium">{selected.created_at ? new Date(selected.created_at).toLocaleDateString() : "-"}</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
